import { useState, useEffect } from 'react';
import { FaCheckCircle, FaRobot, FaChartLine, FaShieldAlt, FaClock, FaChevronLeft, FaChevronRight, FaPause, FaPlay } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const benefits = [
  { text: 'Easy To Install — No experience needed', icon: <FaCheckCircle />, color: '#22c55e' },
  { text: 'Works on MT4, MT5, TradeLocker & more trading platforms', icon: <FaRobot />, color: '#3b82f6' },
  { text: 'Up to 95% ROI monthly with proven performance', icon: <FaChartLine />, color: '#22c55e' },
  { text: 'Compatible with any broker of your choice', icon: <FaCheckCircle />, color: '#3b82f6' },
  { text: 'Trades on multiple currency pairs simultaneously', icon: <FaRobot />, color: '#22c55e' },
  { text: 'Ultra-low drawdown — Maximum 3–5% DD', icon: <FaShieldAlt />, color: '#f59e0b' },
  { text: 'Live trading account & prop firm challenge passer', icon: <FaCheckCircle />, color: '#3b82f6' },
  { text: 'Unlimited License Available for your team', icon: <FaCheckCircle />, color: '#22c55e' },
  { text: '99% winning rate confirmed on live accounts', icon: <FaChartLine />, color: '#22c55e' },
  { text: 'Recovery mode to heal blown accounts', icon: <FaShieldAlt />, color: '#f59e0b' },
  { text: 'Equity protection with accurate risk management', icon: <FaShieldAlt />, color: '#3b82f6' },
  { text: 'Robot operates 24/7 without human intervention', icon: <FaClock />, color: '#22c55e' },
  { text: 'Custom comment names on demand', icon: <FaRobot />, color: '#3b82f6' },
];

export default function BenefitsSlider() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => setActiveIndex((p) => (p + 1) % benefits.length), 3500);
      return () => clearInterval(interval);
    }
  }, [isPaused]);

  const progress = ((activeIndex + 1) / benefits.length) * 100;

  return (
    <section id="benefits" className="py-24 px-6" style={{ background: "var(--uea-black)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// EA Robot Benefits</span>
          <h2 className="font-orbitron font-black text-3xl sm:text-4xl text-white mt-4">
            Why Choose{" "}
            <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              UEA Robot
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Featured slider */}
          <div>
            <div className="rounded-2xl p-0.5 mb-6"
              style={{ background: "linear-gradient(135deg, rgba(34,197,94,0.3), rgba(59,130,246,0.3))" }}>
              <div className="rounded-xl p-8" style={{ background: "var(--uea-card)" }}>
                <div className="w-full h-1 rounded-full mb-6" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <motion.div className="h-full rounded-full"
                    animate={{ width: `${progress}%` }}
                    style={{ background: "linear-gradient(90deg, #22c55e, #3b82f6)" }}
                    transition={{ duration: 0.4 }} />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={activeIndex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}
                    className="min-h-24 flex items-center gap-6">
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-2xl"
                      style={{ background: `${benefits[activeIndex].color}15`, color: benefits[activeIndex].color, border: `1px solid ${benefits[activeIndex].color}30` }}>
                      {benefits[activeIndex].icon}
                    </div>
                    <div>
                      <p className="text-white text-xl font-semibold leading-relaxed font-exo">
                        {benefits[activeIndex].text}
                      </p>
                      <p className="text-slate-500 text-sm mt-2 font-mono-code">
                        Feature {activeIndex + 1} of {benefits.length}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Controls */}
                <div className="flex items-center justify-between mt-6 pt-6 border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                  <button onClick={() => setActiveIndex((activeIndex - 1 + benefits.length) % benefits.length)}
                    className="p-2 rounded-lg text-slate-400 hover:text-green-400 transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)" }}>
                    <FaChevronLeft />
                  </button>
                  <div className="flex gap-1.5">
                    {benefits.map((_, i) => (
                      <button key={i} onClick={() => setActiveIndex(i)}
                        className="rounded-full transition-all duration-300"
                        style={{ width: i === activeIndex ? "20px" : "6px", height: "6px", background: i === activeIndex ? "#22c55e" : "rgba(255,255,255,0.2)" }} />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setIsPaused(!isPaused)}
                      className="p-2 rounded-lg text-slate-400 hover:text-green-400 transition-colors"
                      style={{ background: "rgba(255,255,255,0.05)" }}>
                      {isPaused ? <FaPlay className="text-xs" /> : <FaPause className="text-xs" />}
                    </button>
                    <button onClick={() => setActiveIndex((activeIndex + 1) % benefits.length)}
                      className="p-2 rounded-lg text-slate-400 hover:text-green-400 transition-colors"
                      style={{ background: "rgba(255,255,255,0.05)" }}>
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.slice(0, 8).map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.07 }} whileHover={{ x: 4 }}
                className="flex items-start gap-3 p-4 rounded-xl border cursor-default"
                style={{ borderColor: "rgba(59,130,246,0.1)", background: "var(--uea-card)" }}>
                <span className="mt-0.5 flex-shrink-0" style={{ color: b.color }}>{b.icon}</span>
                <span className="text-slate-300 text-sm leading-tight">{b.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
